import type { Identifier, RaRecord } from "ra-core";
export const FINANCIAL_ZONES = [
  { key: "documentation", label: "Документация", percent: 10 },
  { key: "production", label: "Производство", percent: 30 },
  { key: "administration", label: "Администрирование и логистика", percent: 7 },
  { key: "installation", label: "Сборка и монтаж", percent: 5 },
] as const;
export type ZoneKey = (typeof FINANCIAL_ZONES)[number]["key"];
export type FinanceZone = {
  key: ZoneKey;
  percent: number;
  budget?: number;
  sales_id: Identifier | null;
  company_id: Identifier | null;
};
export interface FinancePlan extends RaRecord {
  deal_id: Identifier;
  revision: number;
  price: number;
  zones: FinanceZone[];
  reason: string;
  created_by_sales_id: Identifier;
  created_at: string;
}
export type FinanceInput = {
  deal_id: Identifier;
  expected_revision: number;
  zones: FinanceZone[];
  reason: string;
};
export const defaultZones = (): FinanceZone[] =>
  FINANCIAL_ZONES.map((z) => ({
    key: z.key,
    percent: z.percent,
    sales_id: null,
    company_id: null,
  }));
export function calculateFinance(price: number, zones: FinanceZone[]) {
  if (!Number.isSafeInteger(price) || price <= 0 || price > 999999999999)
    throw new Error("Укажите положительную целую цену КП в карточке проекта");
  if (
    zones.length !== 4 ||
    FINANCIAL_ZONES.some(
      (z) => zones.filter((v) => v.key === z.key).length !== 1,
    )
  )
    throw new Error("Необходимы четыре финансовые зоны");
  const basis = zones.map((z) => Math.round(z.percent * 100));
  if (
    zones.some(
      (z, i) =>
        !Number.isFinite(z.percent) ||
        z.percent < 0 ||
        z.percent > 100 ||
        Math.abs(z.percent * 100 - basis[i]) > 0.000001,
    )
  )
    throw new Error(
      "Доля зоны должна быть от 0 до 100%, до двух знаков после запятой",
    );
  if (basis.reduce((a, b) => a + b, 0) > 10000)
    throw new Error("Сумма долей зон не должна превышать 100%");
  const cents = BigInt(price) * 100n;
  const budgets = basis.map((b) => Number((cents * BigInt(b)) / 10000n) / 100);
  const allocatedCents = basis.reduce(
    (total, b) => total + (cents * BigInt(b)) / 10000n,
    0n,
  );
  return {
    budgets,
    allocated: Number(allocatedCents) / 100,
    profit: Number(cents - allocatedCents) / 100,
  };
}
